import { useEffect, useState } from 'react';

import { Col, Row } from '@dec/shared';
import { Button } from '@dec/shared';
import { ButtonAction } from '@dec/shared';
import { Combobox, HookFormResolver } from '@dec/shared';
import { type HookFormMethods } from '@dec/shared';
import { BtnGroup } from '@dec/shared';
import { BtnToolbar, PageTitle } from '@dec/shared';
import { useAuthUserContext } from 'app/core/security/authSession';
import { notificationWarning } from 'app/core/toast';
import { FileEarmarkText } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { ObjectRequired } from '../../../core/validations/common';
import { useCatalogByParent } from '../../Parameters/Catalog/Catalog/useCatalog';
import { type StatementHeader, type Year } from '../../Statements/SubmitStatement/types';
import {
  taxReturnAllowed,
  type TaxReturnAllowedParams,
  validPeriodicity
} from '../../Statements/TaxReturn/services/taxReturn.service';
import { useGetAllTaxesTax } from '../../Taxes/Administration/hooks';
import { type Tax } from '../../Taxes/Administration/types';
import { label } from '../../TaxStatement/FormAdministration/translate';
import { getFormVersion } from '../../TaxStatement/FormVersion/http';

export const TaxReturnTaxPeriodPage = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { t, i18n } = useTranslation();
  const [taxes, setTaxes] = useState<Array<any>>([]);
  const [tax, setTax] = useState<Tax>();
  const [years, setYears] = useState<Array<Year>>([]);
  const [parentId, setParentId] = useState('');
  const navigate = useNavigate();
  const { data: taxpayerTax, loading: loadingTaxes, error: errorTaxes } = useGetAllTaxesTax();
  const [{ data: prData, loading: prLoading, error: pdError }] = useCatalogByParent(parentId);

  const validations = yup.object().shape({
    tax: ObjectRequired,
    year: ObjectRequired,
    periodicity: yup.object().test('periodicity', t('common.validations.fieldRequired'), (c) => {
      if (prData && prData.length > 0 && !c) {
        return false;
      }
      return true;
    })
  });

  useEffect(() => {
    if (taxpayerTax) {
      const taxes = taxpayerTax.map((c) => {
        return c.taxPeriodicity.tax;
      });
      setTaxes(taxes);
    }
  }, [taxpayerTax]);
  const auth = useAuthUserContext();
  useEffect(() => {
    const year = new Date().getFullYear();
    const list: Array<Year> = [];
    for (let i = year; i > year - 5; i--) {
      list.push({ id: i, name: i.toString() });
    }
    setYears(list);
  }, []);

  if (loadingTaxes) {
    return <></>;
  }
  const send = async (statement: StatementHeader) => {
    const taxPeriodicitiActive = taxpayerTax?.find(
      (c) => c.taxPeriodicity.taxId === statement.tax?.id
    );

    const requiresPeriodicity = prData !== undefined && prData.length > 0;

    //taxPeriodicityId
    const validPeriodicityResult = await validPeriodicity(
      statement.year.id,
      taxPeriodicitiActive?.taxPeriodicityId,
      statement.periodicity?.id
    );

    if (!validPeriodicityResult) {
      notificationWarning(t('statement-submit-statement.messages.validPeriod'));
      return;
    }

    if (!statement.tax?.id) {
      notificationWarning(t('statement-submit-statement.messages.validPeriod'));
      return;
    }

    const taxpayerId = auth?.userProfile?.taxpayerId;
    if (!statement.year?.id || !taxpayerId) {
      notificationWarning(t('statement-submit-statement.messages.validPeriod'));
      return;
    }

    if (requiresPeriodicity && !statement.periodicity?.id) {
      notificationWarning(t('statement-submit-statement.messages.validPeriod'));
      return;
    }

    if (requiresPeriodicity && !parentId) {
      notificationWarning(t('statement-submit-statement.messages.validPeriod'));
      return;
    }

    const periodicityTypeId = requiresPeriodicity
      ? parentId
      : taxPeriodicitiActive?.taxPeriodicity.periodicityTypeId || parentId;

    if (!periodicityTypeId || !statement.tax?.id) {
      notificationWarning(t('statement-submit-statement.messages.validPeriod'));
      return;
    }

    const params: TaxReturnAllowedParams = {
      taxId: statement.tax.id,
      taxpayerId,
      year: statement.year.id,
      periodicityTypeId,
      ...(statement.periodicity?.id ? { periodicityItemId: statement.periodicity.id } : {})
    };

    const allowed = await taxReturnAllowed(params);
    if (!allowed.allowed) {
      notificationWarning(allowed.message);
      return;
    }

    if (allowed.allowed) {
      // se busca cual es la versión de la declaración por presentar
      const version: any = await getFormVersion(
        statement.tax.id,
        statement.year.id,
        statement.periodicity?.id
      );

      if (!version) {
        notificationWarning(t('statement-submit-statement.messages.formVersionNotFound'));
        return;
      }

      navigate(
        '/tax-returns/create/form/' +
          version.id +
          '?year=' +
          statement.year.id +
          '&periodicityTypeId=' +
          parentId +
          (statement.periodicity ? '&periodicityTypeItemId=' + statement.periodicity.id : '')
      );
    }
  };
  return (
    <>
      <PageTitle
        title={t('statement-submit-statement.title.page')}
        subtitle={t('statement-submit-statement.title.subtitle')}
        icon={FileEarmarkText}
      />

      <HookFormResolver onSubmit={send} validations={validations}>
        {({ formState: { errors }, reset, setValue, getValues, control }: HookFormMethods) => {
          return (
            <>
              <Row>
                <Col>
                  <Combobox
                    label={label(t, 'tax')}
                    name="tax"
                    display="name"
                    code="id"
                    data={taxes}
                    selectFirstOption={true}
                    whenChange={(tax: Tax | null) => {
                      const currentTax = getValues('tax') as Tax | null;

                      if (currentTax?.id === tax?.id) {
                        return;
                      }
                      setValue('year', null);
                      setValue('periodicity', null);

                      if (!tax) {
                        setParentId('');
                        return;
                      }

                      const periodicity = taxpayerTax?.find(
                        (c) => c.taxPeriodicity.taxId === tax.id
                      );
                      if (periodicity?.taxPeriodicity?.periodicityTypeId) {
                        setParentId(periodicity.taxPeriodicity.periodicityTypeId);
                      }
                    }}
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <Combobox
                    label={t('statement-submit-statement.label.year')}
                    name="year"
                    display="name"
                    code="id"
                    data={years}
                  />
                </Col>
                {prData && prData.length > 0 && (
                  <Col className="mt-auto">
                    <Combobox label="" name="periodicity" display="name" code="id" data={prData} />
                  </Col>
                )}
              </Row>
              <BtnToolbar>
                <BtnGroup>
                  <></>
                </BtnGroup>
                <BtnGroup>
                  <Button
                    text={t('statement-submit-statement.label.fileStatement')}
                    action={ButtonAction.save}
                    variant="primary"
                    type={'submit'}
                  />
                </BtnGroup>
              </BtnToolbar>
            </>
          );
        }}
      </HookFormResolver>
    </>
  );
};
